/**
 * FluxPhdProfileSelector - Select Flux PhD efficiency profiles for Site-DNA optimizations
 *
 * Allows users to apply high-fidelity efficiency modeling from Flux PhD simulations
 * to their Site-DNA optimization runs. The selected profile's efficiency factors
 * are applied during preprocessing to adjust battery PCS efficiency.
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Chip,
    Tooltip,
    Link,
    CircularProgress,
    Alert,
    Collapse,
    IconButton,
} from '@mui/material';
import {
    Info as InfoIcon,
    ExpandMore as ExpandMoreIcon,
    Science as ScienceIcon,
} from '@mui/icons-material';
import { useAuth, apiGet, handleApiResponse, vortxColors } from '@vortx/shared-ui';

function FluxPhdProfileSelector({ value, onChange, disabled = false }) {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const { currentUser } = useAuth();

    useEffect(() => {
        loadProfiles();
    }, [currentUser]);

    const loadProfiles = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiGet('/api/site_dna/v1/flux-phd/profiles', currentUser);
            const data = await handleApiResponse(response);
            setProfiles(data.profiles || []);
        } catch (err) {
            console.error('Failed to load Flux PhD profiles:', err);
            setError('Failed to load profiles');
        } finally {
            setLoading(false);
        }
    };

    const selectedProfile = profiles.find(p => p.id === value);

    // Get efficiency color based on value
    const getEfficiencyColor = (efficiency) => {
        if (!efficiency) return 'default';
        if (efficiency >= 95) return 'success';
        if (efficiency >= 90) return 'primary';
        return 'warning';
    };

    return (
        <Box sx={{ mt: 2, mb: 1 }}>
            {/* Header with expand toggle */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    mb: expanded ? 1 : 0,
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <ScienceIcon sx={{ mr: 1, fontSize: 20, color: vortxColors.teal }} />
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    Flux PhD Efficiency Profile
                </Typography>
                <Tooltip title="Apply high-fidelity efficiency modeling from Flux PhD topology simulations">
                    <InfoIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                </Tooltip>
                <IconButton size="small">
                    <ExpandMoreIcon
                        sx={{
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                        }}
                    />
                </IconButton>
            </Box>

            <Collapse in={expanded}>
                <Box sx={{ pl: 3.5 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 1, py: 0 }}>
                            {error}
                        </Alert>
                    )}

                    <FormControl fullWidth size="small" disabled={disabled || loading}>
                        <InputLabel>Select Profile (Optional)</InputLabel>
                        <Select
                            value={value || ''}
                            onChange={(e) => onChange(e.target.value || null)}
                            label="Select Profile (Optional)"
                            startAdornment={loading ? (
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                            ) : null}
                        >
                            <MenuItem value="">
                                <em>None - Use default efficiencies</em>
                            </MenuItem>
                            {profiles.map((profile) => (
                                <MenuItem key={profile.id} value={profile.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <Typography sx={{ flex: 1, fontSize: '0.875rem' }}>
                                            {profile.name}
                                        </Typography>
                                        {profile.overall_efficiency_pct && (
                                            <Chip
                                                label={`${profile.overall_efficiency_pct.toFixed(1)}%`}
                                                size="small"
                                                color={getEfficiencyColor(profile.overall_efficiency_pct)}
                                                sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
                                            />
                                        )}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Selected profile details */}
                    {selectedProfile && (
                        <Box
                            sx={{
                                mt: 1,
                                p: 1.5,
                                bgcolor: 'rgba(95, 212, 196, 0.08)',
                                borderRadius: 1,
                                borderLeft: `3px solid ${vortxColors.teal}`,
                            }}
                        >
                            <Typography variant="caption" sx={{ fontWeight: 600, color: vortxColors.teal }}>
                                {selectedProfile.topology_name || 'Unknown Topology'}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                                Overall Efficiency: {selectedProfile.overall_efficiency_pct?.toFixed(1) || '?'}%
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                                Efficiency factors will be applied to battery storage calculations
                            </Typography>
                        </Box>
                    )}

                    {/* No profiles message */}
                    {profiles.length === 0 && !loading && !error && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            No profiles found.{' '}
                            <Link
                                href="/flux-phd"
                                target="_blank"
                                sx={{ color: vortxColors.teal }}
                            >
                                Create one in Flux PhD
                            </Link>
                        </Typography>
                    )}

                    {/* Help text */}
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: 'block', fontSize: '0.7rem' }}
                    >
                        Flux PhD profiles provide Sandia-model inverter efficiency and topology-specific
                        loss calculations that override default assumptions.
                    </Typography>
                </Box>
            </Collapse>
        </Box>
    );
}

export default FluxPhdProfileSelector;
